/**
 * End-of-Day Review — what the fleet was asked to do, what it did, what it
 * accomplished, where it failed, what the platform decided, and what happens
 * next.
 *
 * Sections 1-5 are deterministic broker-truth aggregation (re-derivable from
 * the database, no model in the loop). Section 6 is the only AI-written block
 * and renders an explicit unavailable state rather than an empty one, so a
 * missing narrative never reads as "nothing to do".
 *
 * Read-only surface over GET /api/reporting/day-review.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Compass,
  Eye,
  Loader2,
  Moon,
  RefreshCw,
  ScrollText,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { reportingApi } from '../lib/api';
import type {
  DayReviewBot,
  DayReviewDecision,
  DayReviewOutcome,
  DayReviewResponse,
} from '../types/titan';

const fmt$ = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v.toFixed(1)}%`;
const fmtNum = (v: number | null | undefined, d = 2) =>
  v === null || v === undefined ? '—' : v.toFixed(d);
const pnlClass = (v: number | null | undefined) =>
  !v ? 'text-muted-foreground' : v > 0 ? 'text-emerald-500' : 'text-rose-500';

/** One row per way a deployed bot can spend a day. `blocked` and
 *  `deployed_silent` are deliberately distinct: one is a gate-tuning problem,
 *  the other a fleet-selection problem, and merging them is how a zero-trade
 *  day gets misdiagnosed. */
const OUTCOME_META: Record<DayReviewOutcome, { label: string; cls: string; icon: React.ReactNode }> = {
  traded_profitable: {
    label: 'Profitable', cls: 'bg-emerald-500/10 text-emerald-500',
    icon: <TrendingUp className="w-3.5 h-3.5" />,
  },
  traded_losing: {
    label: 'Losing', cls: 'bg-rose-500/10 text-rose-500',
    icon: <TrendingDown className="w-3.5 h-3.5" />,
  },
  traded_flat: {
    label: 'Flat', cls: 'bg-slate-500/10 text-slate-400',
    icon: <ArrowRight className="w-3.5 h-3.5" />,
  },
  blocked: {
    label: 'Blocked', cls: 'bg-amber-500/10 text-amber-500',
    icon: <Ban className="w-3.5 h-3.5" />,
  },
  deployed_silent: {
    label: 'Never fired', cls: 'bg-violet-500/10 text-violet-400',
    icon: <Moon className="w-3.5 h-3.5" />,
  },
  not_order_capable: {
    label: 'Shadow (by design)', cls: 'bg-sky-500/10 text-sky-400',
    icon: <Eye className="w-3.5 h-3.5" />,
  },
};

const VERDICT_META: Record<string, { label: string; cls: string }> = {
  worked: { label: 'Worked', cls: 'bg-emerald-500/10 text-emerald-500' },
  backfired: { label: 'Backfired', cls: 'bg-rose-500/10 text-rose-500' },
  flat: { label: 'Flat', cls: 'bg-slate-500/10 text-slate-400' },
  too_early: { label: 'Too early', cls: 'bg-amber-500/10 text-amber-500' },
  not_measurable: { label: 'Not measurable', cls: 'bg-slate-500/10 text-slate-400' },
};

const Pill: React.FC<{ cls: string; children: React.ReactNode }> = ({ cls, children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
    {children}
  </span>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; cls?: string }> = (
  { label, value, hint, cls },
) => (
  <div className="rounded-lg border border-border/60 bg-card/40 px-3 py-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${cls || ''}`}>{value}</div>
    {hint ? <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div> : null}
  </div>
);

/** A horizontal breakdown bar — used for gates and exit reasons, where the
 *  shape of the distribution matters more than the exact counts. */
const Breakdown: React.FC<{ data: Record<string, number>; money?: boolean }> = ({ data, money }) => {
  const entries = Object.entries(data || {});
  if (!entries.length) return <span className="text-muted-foreground text-sm">—</span>;
  const max = Math.max(...entries.map(([, v]) => Math.abs(v))) || 1;
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <div className="w-40 shrink-0 truncate text-xs text-muted-foreground" title={k}>{k}</div>
          <div className="flex-1 h-2 rounded bg-muted/40 overflow-hidden">
            <div
              className={`h-full rounded ${money ? (v < 0 ? 'bg-rose-500/70' : 'bg-emerald-500/70') : 'bg-sky-500/60'}`}
              style={{ width: `${Math.max(2, (Math.abs(v) / max) * 100)}%` }}
            />
          </div>
          <div className={`w-20 shrink-0 text-right text-xs tabular-nums ${money ? pnlClass(v) : ''}`}>
            {money ? fmt$(v) : v.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

const DecisionRows: React.FC<{ rows: DayReviewDecision[]; scored?: boolean }> = ({ rows, scored }) => (
  <Table>
    <TableHeader>
      <TableRow>
        {scored ? <TableHead className="w-24">Day</TableHead> : null}
        <TableHead>Decision</TableHead>
        <TableHead>Subject</TableHead>
        <TableHead className="w-44">Change</TableHead>
        <TableHead className="w-24">Mode</TableHead>
        <TableHead>{scored ? 'Outcome' : 'Why'}</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((d, i) => (
        <TableRow key={`${d.decision_type}-${d.subject_id}-${i}`}>
          {scored ? (
            <TableCell className="text-xs text-muted-foreground">{d.trade_date}</TableCell>
          ) : null}
          <TableCell className="text-sm font-medium">
            {d.decision_type.replace(/_/g, ' ')}
          </TableCell>
          <TableCell className="text-sm">
            <div className="truncate max-w-[220px]" title={d.subject_id}>
              {d.subject_label || d.subject_id}
            </div>
            <div className="text-[11px] text-muted-foreground">{d.subject_kind}</div>
          </TableCell>
          <TableCell className="text-xs tabular-nums">
            <span className="uppercase font-medium">{d.action}</span>
            {d.from_value || d.to_value ? (
              <div className="text-muted-foreground">
                {d.from_value || '—'} → {d.to_value || '—'}
              </div>
            ) : null}
          </TableCell>
          <TableCell>
            <Pill cls={d.applied ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 text-slate-400'}>
              {d.applied ? 'applied' : d.mode}
            </Pill>
          </TableCell>
          <TableCell className="text-xs text-muted-foreground">
            {scored ? (
              <div className="space-y-1">
                <Pill cls={VERDICT_META[d.verdict || 'not_measurable']?.cls || ''}>
                  {VERDICT_META[d.verdict || 'not_measurable']?.label}
                </Pill>
                <div>{d.verdict_detail}</div>
              </div>
            ) : (
              d.rationale || '—'
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const DayReview: React.FC = () => {
  const [data, setData] = useState<DayReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState<string>('');

  const load = useCallback(async (d?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportingApi.dayReview(d || undefined);
      setData(res);
      setDate(res.trade_date);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the day review');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const shiftDay = (delta: number) => {
    if (!data) return;
    const d = new Date(`${data.trade_date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta);
    void load(d.toISOString().slice(0, 10));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Building the day review…
      </div>
    );
  }

  const h = data?.headline;
  const f = data?.failures;
  const fp = data?.forward_plan;
  const traded = (data?.fleet || []).filter((b) => b.outcome.startsWith('traded'));
  const idle = (data?.fleet || []).filter((b) => !b.outcome.startsWith('traded'));

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ScrollText className="w-6 h-6" /> End-of-Day Review
          </h1>
          <p className="text-sm text-muted-foreground">
            What the fleet was asked to do, what it did, and what changes next.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftDay(-1)}>← Prev</Button>
          <div className="flex items-center gap-1 text-sm font-medium px-2">
            <CalendarDays className="w-4 h-4" /> {data?.trade_date || '—'}
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftDay(1)}>Next →</Button>
          <Button variant="outline" size="sm" onClick={() => void load(date)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {data?.degraded_sources?.length ? (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            <strong>Incomplete data.</strong> These sources failed to load:{' '}
            {data.degraded_sources.join(', ')}. The figures below are missing
            those inputs — this is not a quiet day.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Headline */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Net P&L" value={fmt$(h?.pnl)} cls={pnlClass(h?.pnl)}
              hint="broker truth" />
        <Stat label="Round trips" value={h?.n ?? 0} hint="logical, chunk-collapsed" />
        <Stat label="Win rate" value={fmtPct(h?.win_rate)} />
        <Stat label="Profit factor" value={fmtNum(h?.profit_factor, 2)} />
        <Stat label="Avg win / loss" value={`${fmt$(h?.avg_win)} / ${fmt$(h?.avg_loss)}`} />
        <Stat label="Bots deployed" value={f?.n_deployed ?? 0}
              hint={`${f?.n_order_capable ?? 0} order-capable`} />
      </div>

      {/* 6. Forward plan — first, because it is what the operator acts on. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Compass className="w-4 h-4" /> What happens next
            {fp?.confidence ? (
              <Pill cls="bg-slate-500/10 text-slate-400">{fp.confidence} confidence</Pill>
            ) : null}
          </CardTitle>
          <CardDescription>
            The only AI-written section. Written from the deterministic numbers below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fp?.available ? (
            <div className="space-y-4">
              {fp.one_line ? (
                <p className="text-sm font-medium border-l-2 border-primary/60 pl-3">{fp.one_line}</p>
              ) : null}
              <div className="grid md:grid-cols-3 gap-4">
                {([
                  ['Tomorrow', fp.tomorrow],
                  ['This week', fp.this_week],
                  ['Watching', fp.watch_items],
                ] as [string, string[] | undefined][]).map(([title, items]) => (
                  <div key={title}>
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">{title}</div>
                    <ul className="space-y-1.5">
                      {(items || []).length ? (items || []).map((x, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-muted-foreground">·</span><span>{x}</span>
                        </li>
                      )) : <li className="text-sm text-muted-foreground">—</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                Forward plan unavailable — {fp?.reason || 'unknown'}. The
                deterministic sections below are unaffected.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* 1-3. Fleet that traded */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bots that placed capital ({traded.length})</CardTitle>
          <CardDescription>Broker-truth results per bot for this session.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {traded.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bot</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead className="text-right">P&L</TableHead>
                  <TableHead className="text-right">n</TableHead>
                  <TableHead className="text-right">WR</TableHead>
                  <TableHead className="text-right">PF</TableHead>
                  <TableHead className="text-right">Best / worst</TableHead>
                  <TableHead>Exits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {traded.map((b: DayReviewBot) => (
                  <TableRow key={b.pattern_id}>
                    <TableCell>
                      <div className="font-medium text-sm">{b.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {b.template} · {b.tier}
                        {b.preserved ? ' · preserved' : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Pill cls={OUTCOME_META[b.outcome].cls}>
                        {OUTCOME_META[b.outcome].icon}{OUTCOME_META[b.outcome].label}
                      </Pill>
                    </TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${pnlClass(b.pnl)}`}>
                      {fmt$(b.pnl)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{b.n}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtPct(b.win_rate)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtNum(b.profit_factor)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      <span className="text-emerald-500">{fmt$(b.best_trade)}</span>
                      {' / '}
                      <span className="text-rose-500">{fmt$(b.worst_trade)}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {Object.entries(b.exit_reasons || {})
                        .map(([k, v]) => `${k}×${v}`).join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">
              No bot placed capital on this session.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 4. Where it failed */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Bots that placed no capital ({idle.length})
            </CardTitle>
            <CardDescription>
              “Never fired” is a fleet-selection problem; “blocked” is a gate problem.
              Shadow-tier bots are working as designed.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {idle.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bot</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                    <TableHead>Top reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {idle.map((b) => (
                    <TableRow key={b.pattern_id}>
                      <TableCell>
                        <div className="text-sm">{b.name}</div>
                        <div className="text-[11px] text-muted-foreground">{b.template} · {b.tier}</div>
                      </TableCell>
                      <TableCell>
                        <Pill cls={OUTCOME_META[b.outcome].cls}>
                          {OUTCOME_META[b.outcome].icon}{OUTCOME_META[b.outcome].label}
                        </Pill>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {b.n_rejections.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {b.top_block_reason || '—'}
                        {b.n_pre_match_gated ? (
                          <div className="text-[11px] opacity-70">
                            +{b.n_pre_match_gated.toLocaleString()} pre-match
                            (out of session / regime)
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">Every deployed bot traded.</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Gates that blocked entries</CardTitle>
            </CardHeader>
            <CardContent><Breakdown data={f?.top_gates || {}} /></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">P&amp;L by exit reason</CardTitle>
              <CardDescription>
                Where the money was actually made and given back.
              </CardDescription>
            </CardHeader>
            <CardContent><Breakdown data={f?.pnl_by_exit_reason || {}} money /></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">P&amp;L by entry hour (ET)</CardTitle>
            </CardHeader>
            <CardContent><Breakdown data={h?.pnl_by_entry_hour_et || {}} money /></CardContent>
          </Card>
        </div>
      </div>

      {f?.worst_symbol_pct_of_gross_loss && f.worst_symbol_pct_of_gross_loss >= 50 ? (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            Loss concentration: <strong>{f.worst_symbol}</strong> accounts for{' '}
            {f.worst_symbol_pct_of_gross_loss}% of the day's gross loss.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* 5. Decisions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Decisions made today
            <Pill cls="bg-slate-500/10 text-slate-400">
              {data?.decisions_summary?.n ?? 0} total · {data?.decisions_summary?.applied ?? 0} applied
            </Pill>
          </CardTitle>
          <CardDescription>
            Every autonomous change the platform made to how it will trade.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {data?.decisions_today?.length ? (
            <DecisionRows rows={data.decisions_today} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No autonomous decisions were recorded for this day. The learners are
              evidence-gated, so quiet days are expected — but note the ledger only
              carries decisions made after it shipped.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Earlier decisions — did they work?
          </CardTitle>
          <CardDescription>
            Scored against realized broker truth since. Decisions that retired
            capital show as “not measurable” on purpose: the trades they
            prevented left no trace to score.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {data?.prior_decisions_scored?.length ? (
            <DecisionRows rows={data.prior_decisions_scored} scored />
          ) : (
            <p className="text-sm text-muted-foreground">
              No prior decisions in the scoring window yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* How to read this */}
      <Card className="bg-muted/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">How to read this report</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1">
            {(data?.data_notes || []).map((n, i) => (
              <li key={i} className="text-xs text-muted-foreground flex gap-2">
                <span>·</span><span>{n}</span>
              </li>
            ))}
          </ul>
          {data?.orphan_trade_pattern_ids?.length ? (
            <p className="text-xs text-amber-500 mt-2">
              {data.orphan_trade_pattern_ids.length} trade(s) could not be attributed to a
              deployed bot — attribution may be degraded.
            </p>
          ) : null}
          <p className="text-[11px] text-muted-foreground mt-2">
            Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DayReview;
